import React, { useState } from "react";
import {
  ActivityData,
  Activity as ActivityType
} from "@/entities/activities/types";
import Text from "@/components/ui/text";
import { formatMonthYearDateTime } from "@/utils/datetime";
import { cn } from "@/utils/utils";
import Activity from "@/components/layouts/Activities/components/Activity";
import { ISOStringFormat } from "date-fns";
import IconContainer from "@/components/ui/icon-container";
import { ChevronDownMenu } from "@dreamhub/designsystem/icons";
import { map } from "lodash";

export const UPCOMING_MONTH = "0000-00";

interface Props {
  month: string;
  activities: ActivityData[];
  searchQuery: string;
  openEdit: (activityType: ActivityType, activityData: ActivityData) => void;
  openDelete: (activityId: string) => void;
  onDropdownOpenChange: (open: boolean) => void;
  isTasksDrawerOpen: boolean;
  expandedActivityId?: string | null;
  clearExpandedActivityId?: () => void;
  customTitle?: string;
  onImportTranscript?: (activity: ActivityData) => void;
  onExportTranscript?: (activity: ActivityData) => void;
  onInviteNotetaker?: (activity: ActivityData) => void;
  onRemoveTag?: (activity: ActivityData, tagId: string) => void;
}

const ActivityMonth: React.FC<Props> = ({
  month,
  activities,
  searchQuery,
  openEdit,
  openDelete,
  onDropdownOpenChange,
  isTasksDrawerOpen,
  expandedActivityId,
  clearExpandedActivityId,
  customTitle,
  onImportTranscript,
  onExportTranscript,
  onInviteNotetaker,
  onRemoveTag
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const createFormattedMonth = (monthStr: string): ISOStringFormat => {
    // Handle special case for upcoming activities
    if (monthStr === UPCOMING_MONTH) {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
      return `${currentMonth}-15T12:00:00.000Z` as ISOStringFormat;
    }

    const monthRegex = /^\d{4}-\d{2}$/;
    if (!monthRegex.test(monthStr)) {
      console.warn(
        `Invalid month format provided to ActivityMonth: ${monthStr}`
      );
      const now = new Date();
      const fallbackMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
      return `${fallbackMonth}-15T12:00:00.000Z` as ISOStringFormat;
    }
    return `${monthStr}-15T12:00:00.000Z` as ISOStringFormat;
  };

  const formattedMonth = createFormattedMonth(month);

  return (
    <div className="flex flex-col gap-8">
      <div
        className="flex items-center justify-center group w-full cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Text
          variant="bodySecondary"
          className="uppercase flex gap-1 self-center px-2 py-1 group-hover:text-h2 transition-colors duration-200 ease-in-out"
        >
          <IconContainer
            color="secondary"
            className={cn(
              "rounded-full transition-all duration-200 flex items-center justify-center group-hover:text-h2 [&>svg]:fill-current",
              !isOpen && "-rotate-90"
            )}
          >
            <ChevronDownMenu />
          </IconContainer>
          {customTitle || formatMonthYearDateTime(formattedMonth)}
        </Text>
      </div>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-8">
            {map(activities, (activity, index) => (
              <Activity
                key={activity.id}
                activity={activity}
                isLast={index === activities.length - 1}
                searchQuery={searchQuery}
                openEdit={openEdit}
                openDelete={openDelete}
                onDropdownOpenChange={onDropdownOpenChange}
                isTasksDrawerOpen={isTasksDrawerOpen}
                expandedActivityId={expandedActivityId}
                clearExpandedActivityId={clearExpandedActivityId}
                isOnlyActivity={activities.length === 1}
                isUpcoming={month === UPCOMING_MONTH}
                onImportTranscript={onImportTranscript}
                onExportTranscript={onExportTranscript}
                onInviteNotetaker={onInviteNotetaker}
                onRemoveTag={onRemoveTag}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityMonth;
